/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// TODO: Move isDeliverySection to a lib package to fix layering violation
// eslint-disable-next-line jira/import/no-restricted-import
import { isDeliverySection } from '@jira/polaris__component-idea-view-sections/src/isDeliverySection.tsx';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import {
	fireScreenAnalytics,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { CrossSiteSourceInstanceType } from './insightCreate.tsx';
import { sendPendoTrackEvent } from './sendPendoTrackEvent.tsx';

export const IdeaView = {
	copyIdeaLinkButtonClicked: (event: UIAnalyticsEvent): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/47650
		fireUIAnalytics(event, 'button clicked', 'copyIdeaLinkButton');
	},
	copyIdeaCommentLinkButtonClicked: (event: UIAnalyticsEvent): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/48687
		fireUIAnalytics(event, 'button clicked', 'copyIdeaCommentLinkButton');
	},
	copyIdeaInsightLinkButtonClicked: (event: UIAnalyticsEvent): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/50374
		fireUIAnalytics(event, 'icon clicked', 'copyIdeaInsightLink');
	},
	viewed: (event: UIAnalyticsEvent): void => {
		// https://app.pendo.io/s/4758009750487040/trackevents/n5034S7jQww7n-LViD6ZpHEP3xM
		sendPendoTrackEvent('idea_opened');

		// Fullscreen: https://data-portal.internal.atlassian.com/analytics/registry/893
		// Sidebar: https://data-portal.internal.atlassian.com/analytics/registry/51086
		fireScreenAnalytics(event);
	},
	tabClicked: (event: UIAnalyticsEvent, issueViewSection: string): void => {
		let actionSubjectId: string = issueViewSection;

		if (issueViewSection === 'capture') {
			actionSubjectId = 'insights';
		} else if (isDeliverySection(issueViewSection)) {
			actionSubjectId = 'delivery';
		}

		fireUIAnalytics(event, 'tab clicked', actionSubjectId);
	},
	linkIssue: (
		event: UIAnalyticsEvent,
		{
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
		}: {
			isRemoteLinked: boolean;
			renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
			sourceInstanceType?: CrossSiteSourceInstanceType;
		},
	): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/59591
		fireTrackAnalytics(event, 'issueLink added', {
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
		});
	},
	linkIssueNew: (
		event: UIAnalyticsEvent,
		{
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
			issueLinkTypeName,
			projectType,
		}: {
			isRemoteLinked: boolean;
			renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
			sourceInstanceType?: CrossSiteSourceInstanceType;
			issueLinkTypeName?: string;
			projectType?: string;
		},
	): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/59591
		fireTrackAnalytics(event, 'issueLink added', {
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
			issueLinkTypeName,
			projectType,
		});
	},
	unlinkIssue: (
		event: UIAnalyticsEvent,
		{
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
		}: {
			isRemoteLinked: boolean;
			renderingLocation?: 'idea-view' | 'tab-overview' | 'tab-delivery';
			sourceInstanceType?: CrossSiteSourceInstanceType;
		},
	): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/59593
		fireTrackAnalytics(event, 'issueLink deleted', {
			isRemoteLinked,
			renderingLocation,
			sourceInstanceType,
		});
	},
	DeliveryTab: {
		createDeliveryTicketButtonClicked: (event: UIAnalyticsEvent, isFromHint = false): void => {
			sendPendoTrackEvent('ideaViewDeliveryCreateDeliveryTicketButton clicked');

			// https://data-portal.internal.atlassian.com/analytics/registry/50485
			fireUIAnalytics(event, 'button clicked', 'createDeliveryTicket', { isFromHint });
		},
		linkDeliveryTicketButtonClicked: (event: UIAnalyticsEvent, isFromHint = false): void => {
			sendPendoTrackEvent('ideaViewDeliveryLinkDeliveryTicketButton clicked');

			// https://data-portal.internal.atlassian.com/analytics/registry/50507
			fireUIAnalytics(event, 'button clicked', 'addDeliveryTicket', { isFromHint });
		},
		deliveryTicketsHintClosed: (event: UIAnalyticsEvent): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50733
			fireUIAnalytics(event, 'button clicked', 'closeDeliveryTicketsHint');
		},
		learnMoreButtonClicked: (event: UIAnalyticsEvent, isFromHint = false): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50732
			fireUIAnalytics(event, 'button clicked', 'learnMoreAboutDeliveryTickets', {
				isFromHint,
			});
		},
		deliveryTicketCreated: (
			event: UIAnalyticsEvent,
			{
				createdIssueId,
				createdIssueProjectId,
				embedIdeaInDescription,
				isRemoteLinked,
				sourceInstanceType,
			}: {
				createdIssueId: string;
				createdIssueProjectId: string;
				embedIdeaInDescription: boolean;
				isRemoteLinked: boolean;
				sourceInstanceType?: CrossSiteSourceInstanceType;
			},
		): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/61825
			fireTrackAnalytics(event, 'deliveryTicket created', {
				createdIssueId,
				createdIssueProjectId,
				embedIdeaInDescription,
				isRemoteLinked,
				sourceInstanceType,
			});
		},
	},
	InsightsTab: {
		createInsightButtonClicked: (event: UIAnalyticsEvent, isFromHint = false): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50336
			fireUIAnalytics(event, 'button clicked', 'createInsight', { isFromHint });
		},
		createInsightHintClosed: (event: UIAnalyticsEvent): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50739
			fireUIAnalytics(event, 'button clicked', 'closeCreateInsightsHint');
		},
		createInsightLearnMoreButtonClicked: (event: UIAnalyticsEvent, isFromHint = false): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50738
			fireUIAnalytics(event, 'button clicked', 'learnMoreCreateInsightsTickets', {
				isFromHint,
			});
		},
		addLinkToInsightsHintClosed: (event: UIAnalyticsEvent): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50741
			fireUIAnalytics(event, 'button clicked', 'closeAddLinkToInsightsHint');
		},
		addLinkToInsightsLearnMoreButtonClicked: (
			event: UIAnalyticsEvent,
			isFromHint = false,
		): void => {
			// https://data-portal.internal.atlassian.com/analytics/registry/50740
			fireUIAnalytics(event, 'button clicked', 'learnMoreAddLinkToInsights', {
				isFromHint,
			});
		},
	},
};
